import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { staffReportsService } from '../../services/carehome_manager';
import {
  Modal, Select, Button, PageHeader, DataTable, Person, StatusPill, DocumentViewer,
} from '../../components';
import { toast } from 'react-hot-toast';
import {
  Inbox, Clock, AlertTriangle, CheckCircle2, Download, Megaphone, MessageSquare,
  Paperclip, Lock, ChevronRight, Wrench,
} from 'lucide-react';

/**
 * Staff Reports — non-resident operational issues raised by staff from the
 * mobile app (broken equipment, uncovered shift, unsafe area). Resident-related
 * events stay in Incidents; a staff report never carries resident health data.
 *
 * Managers triage here: set status, assign an owner, flag priority, reply to the
 * reporter, keep internal notes the reporter can't see, and turn a report into a
 * staff-wide announcement. Backed by /api/carehome-manager/staff-reports.
 */

const API_ORIGIN = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const fileHref = (path) => (/^https?:\/\//i.test(path || '') ? path : `${API_ORIGIN}${path}`);
// A report's files: the list, or the single attachment of an older report.
const attachmentsOf = (r) => (r?.attachments?.length
  ? r.attachments
  : r?.attachmentUrl ? [{ url: r.attachmentUrl, name: r.attachmentName || 'Attachment' }] : []);

// Same words as the app: Open → In progress → Resolved; Closed files it away.
// Stored values are unchanged (api/utils/staffReportService.js STATUS_LABELS).
const STATUS_OPTIONS = [
  { value: 'new', label: 'Open' },
  { value: 'in_review', label: 'In progress' },
  { value: 'actioned', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

// The app's categories. Filtering by one also finds reports filed under its
// older name (e.g. Utilities → Maintenance & repairs) — the API maps them.
const CATEGORY_OPTIONS = [
  'Maintenance & repairs',
  'Cleaning & hygiene',
  'Supplies & stock',
  'Staffing & rota',
  'Safety & security',
  'Other',
];

// The maintenance team's list (api/utils/staffReportService.js) is everything
// assigned to Maintenance — Maintenance & repairs, and Safety & security when
// the reporter ticked "Maintenance action needed", go there automatically.
const MAINTENANCE_CATEGORIES = ['Maintenance & repairs', 'Safety & security'];

// Owners a report can be handed to — individual managers aren't listed here
// because assignment is free text on the API; these are the standing teams.
// "Maintenance team" puts it on the maintenance list (and tells them);
// "Manager review" takes it off.
const ASSIGNEE_OPTIONS = [
  { value: '', label: 'Manager review' },
  { value: 'Maintenance', label: 'Maintenance team' },
  { value: 'Facilities', label: 'Facilities' },
  { value: 'HR & Rota', label: 'HR & Rota' },
  { value: 'Management', label: 'Management' },
];

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All staff' },
  { value: 'by_role', label: 'Carers only' },
];

const STATUS_TONE = {
  new: 'info',
  in_review: 'warn',
  actioned: 'good',
  closed: 'mute',
};

const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

// Metric card — same shape as the Compliance board's tiles.
const Metric = ({ label, value, hint, icon: Icon, tone }) => (
  <div className="bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)] p-5">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[11px] font-bold tracking-wider text-[#94A3B8] dark:text-gray-500 uppercase">{label}</div>
        <div className="text-[34px] leading-none font-extrabold text-[#0B1220] dark:text-gray-100 mt-3 tabular-nums">{value}</div>
        <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-2">{hint}</div>
      </div>
      <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${tone}`}>
        <Icon className="w-5 h-5" strokeWidth={1.9} />
      </span>
    </div>
  </div>
);

// One entry in the report timeline.
const TimelineEntry = ({ entry }) => (
  <div className="flex gap-3">
    <span className="w-8 h-8 shrink-0 rounded-xl grid place-items-center text-[11px] font-extrabold bg-[#EEF3FF] dark:bg-blue-900/20 text-blue-600 dark:text-blue-300">
      {entry.authorInitials}
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-[13px] text-[#334155] dark:text-gray-300 leading-relaxed">
        <span className="font-bold text-[#0B1220] dark:text-gray-100">{entry.author}</span>
        {' · '}
        {entry.message}
        {!entry.visible && (
          <span className="ml-2 inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wide text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 px-2 py-0.5 rounded-full align-middle">
            <Lock className="w-3 h-3" /> Internal
          </span>
        )}
      </div>
      <div className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 mt-0.5">{fmtDateTime(entry.createdAt)}</div>
    </div>
  </div>
);

const StaffReports = () => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({ new: 0, inProgress: 0, urgent: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  // 'all' (management) or 'maintenance' (the maintenance team) — decided by the
  // API from the caller's access, never by this page.
  const [scope, setScope] = useState('all');
  const maintenanceOnly = scope === 'maintenance';

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Detail modal
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reply, setReply] = useState('');
  const [replyVisible, setReplyVisible] = useState(true);

  // Announcement modal
  const [announceOpen, setAnnounceOpen] = useState(false);
  const [announceBody, setAnnounceBody] = useState('');
  const [announceAudience, setAnnounceAudience] = useState('all');
  // An attachment opened in place (DocumentViewer), never a new tab.
  const [preview, setPreview] = useState(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await staffReportsService.list({
        search,
        status: statusFilter,
        priority: priorityFilter,
        category: categoryFilter,
      });
      if (res.success) {
        setReports(res.data || []);
        setStats(res.stats || {});
        setScope(res.scope === 'maintenance' ? 'maintenance' : 'all');
      } else {
        toast.error(res.message || 'Failed to load staff reports');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load staff reports');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, priorityFilter, categoryFilter]);

  // Debounced so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(fetchReports, 300);
    return () => clearTimeout(timer);
  }, [fetchReports]);

  // Keep the open modal in step with the list after any mutation.
  const applyUpdated = (updated) => {
    setSelected(updated);
    setReports((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    fetchReports();
  };

  const patchReport = async (payload) => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await staffReportsService.update(selected.id, payload);
      if (res.success) {
        applyUpdated(res.data);
        toast.success(res.message || 'Report updated');
      } else {
        toast.error(res.message || 'Failed to update');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    if (!selected || !reply.trim()) {
      toast.error('Write an update before sending');
      return;
    }
    setSaving(true);
    try {
      const res = await staffReportsService.addUpdate(selected.id, {
        message: reply.trim(),
        visible: replyVisible,
      });
      if (res.success) {
        applyUpdated(res.data);
        setReply('');
        toast.success(replyVisible ? 'Update sent to the reporter' : 'Internal note saved');
      } else {
        toast.error(res.message || 'Failed to add update');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add update');
    } finally {
      setSaving(false);
    }
  };

  const openAnnouncement = () => {
    if (!selected) return;
    setAnnounceAudience('all');
    setAnnounceBody(
      `${selected.summary}${selected.location ? ` — ${selected.location}` : ''}. Please follow local management instructions.`
    );
    setAnnounceOpen(true);
  };

  const publishAnnouncement = async () => {
    if (!announceBody.trim()) {
      toast.error('Add announcement text before publishing');
      return;
    }
    setSaving(true);
    try {
      const res = await staffReportsService.createAnnouncement(selected.id, {
        body: announceBody.trim(),
        audience_type: announceAudience,
        audience_roles: announceAudience === 'by_role' ? ['carers'] : undefined,
      });
      if (res.success) {
        applyUpdated(res.data);
        setAnnounceOpen(false);
        toast.success('Staff announcement created');
      } else {
        toast.error(res.message || 'Failed to publish');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to publish');
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    if (!reports.length) {
      toast.error('Nothing to export');
      return;
    }
    const headers = ['Reference', 'Summary', 'Category', 'Location', 'Priority', 'Status', 'Reporter', 'Assigned to', 'Submitted'];
    const rows = reports.map((r) => [
      r.reference, r.summary, r.category, r.location, r.priority, r.statusLabel,
      r.reporter, r.assignedTo, r.submittedAt,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((v) => `"${String(v ?? '').replaceAll('"', '""')}"`).join(','))
      .join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    link.download = 'servpatch-staff-reports.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
    toast.success(`Exported ${rows.length} report(s)`);
  };

  const columns = useMemo(() => [
    {
      key: 'summary',
      header: 'Report',
      sortable: true,
      render: (row) => (
        <div className="min-w-0">
          <div className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100 leading-tight truncate">
            {row.summary}
          </div>
          <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 truncate flex items-center gap-1.5">
            {row.reference}
            {row.location ? ` · ${row.location}` : ''}
            {attachmentsOf(row).length > 0 && (
              <span className="inline-flex items-center gap-0.5 shrink-0">
                <Paperclip className="w-3 h-3" />{attachmentsOf(row).length > 1 ? attachmentsOf(row).length : ''}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'reporter',
      header: 'Reporter',
      sortable: true,
      render: (row) => <Person name={row.reporter} sub={`→ ${row.assignedLabel || row.assignedTo || 'Manager review'}`} />,
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      render: (row) => (
        <div className="flex flex-col items-start gap-1">
          <span className="text-[13px] text-[#334155] dark:text-gray-300">{row.category}</span>
          {/* Which reports the maintenance team sees — for management only;
              on the maintenance team's own page every row is one. */}
          {row.maintenance && !maintenanceOnly && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#F1F5F9] dark:bg-[#31373f] text-[#475569] dark:text-gray-300 text-[10.5px] font-bold">
              <Wrench className="w-3 h-3" /> Maintenance
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      render: (row) => (
        <StatusPill tone={row.priority === 'urgent' ? 'crit' : 'mute'}>
          {row.priority === 'urgent' ? 'Urgent' : 'Normal'}
        </StatusPill>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => <StatusPill tone={STATUS_TONE[row.status] || 'mute'}>{row.statusLabel}</StatusPill>,
    },
    {
      key: 'submittedAt',
      header: 'Submitted',
      sortable: true,
      render: (row) => (
        <span className="text-[13px] text-[#64748B] dark:text-gray-400 whitespace-nowrap">
          {fmtDateTime(row.submittedAt)}
        </span>
      ),
    },
    {
      key: 'open',
      header: '',
      align: 'right',
      render: () => <ChevronRight className="w-4 h-4 text-[#94A3B8] inline-block" />,
    },
  ], [maintenanceOnly]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title={maintenanceOnly ? 'Maintenance reports' : 'Staff Reports'}
        count={stats.total}
        subtitle={maintenanceOnly
          ? 'Reports assigned to your maintenance team — Maintenance & repairs, Safety & security where maintenance is needed, and anything management hands over. Update the status as you work and keep the reporter informed.'
          : 'Operational issues raised by staff. Review, assign an owner and keep the reporter updated.'}
        actions={(
          <Button variant="secondary" leftIcon={<Download className="w-4 h-4" />} onClick={exportCsv}>
            Export CSV
          </Button>
        )}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Metric
          label="Open" value={stats.new ?? 0} hint="Not yet picked up" icon={Inbox}
          tone="bg-[#EEF3FF] text-blue-600 dark:bg-blue-900/20 dark:text-blue-300"
        />
        <Metric
          label="In progress" value={stats.inProgress ?? 0} hint="In progress or resolved, not closed" icon={Clock}
          tone="bg-[#FFFBEB] text-amber-600 dark:bg-amber-900/10 dark:text-amber-400"
        />
        <Metric
          label="Urgent" value={stats.urgent ?? 0} hint="Open and flagged urgent" icon={AlertTriangle}
          tone="bg-[#FFF1F3] text-rose-600 dark:bg-rose-900/20 dark:text-rose-400"
        />
        <Metric
          label="Closed" value={stats.closed ?? 0} hint="Resolved and filed away" icon={CheckCircle2}
          tone="bg-[#ECFDF5] text-emerald-600 dark:bg-emerald-900/10 dark:text-emerald-400"
        />
      </div>

      <DataTable
        columns={columns}
        data={reports}
        loading={loading}
        rowKey={(row) => row.id}
        onRowClick={(row) => { setReply(''); setReplyVisible(true); setSelected(row); }}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search reference, report or staff member"
        emptyIcon={Inbox}
        emptyTitle="No staff reports"
        emptyText="Reports submitted from the mobile app will appear here."
        filters={(
          <>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              size="sm"
              className="w-[150px]"
              options={[{ value: 'all', label: 'All statuses' }, ...STATUS_OPTIONS]}
            />
            <Select
              value={priorityFilter}
              onChange={setPriorityFilter}
              size="sm"
              className="w-[150px]"
              options={[
                { value: 'all', label: 'All priorities' },
                { value: 'urgent', label: 'Urgent' },
                { value: 'normal', label: 'Normal' },
              ]}
            />
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              size="sm"
              className="w-[190px]"
              options={maintenanceOnly
                ? [
                  { value: 'all', label: 'All maintenance' },
                  ...MAINTENANCE_CATEGORIES.map((c) => ({ value: c, label: c })),
                ]
                : [
                  { value: 'all', label: 'All categories' },
                  // The maintenance team's list in one pick: the three
                  // maintenance categories plus anything assigned to Maintenance.
                  { value: 'maintenance', label: 'Maintenance' },
                  ...CATEGORY_OPTIONS.map((c) => ({ value: c, label: c })),
                ]}
            />
          </>
        )}
      />

      {/* ── Report detail ─────────────────────────────────────────────── */}
      <Modal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? `${selected.reference} · ${selected.summary}` : ''}
        maxWidth="max-w-3xl"
        closeOnBackdrop
      >
        {selected && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusPill tone={STATUS_TONE[selected.status] || 'mute'}>{selected.statusLabel}</StatusPill>
              <StatusPill tone={selected.priority === 'urgent' ? 'crit' : 'mute'}>
                {selected.priority === 'urgent' ? 'Urgent' : 'Normal priority'}
              </StatusPill>
              <span className="text-[12px] text-[#94A3B8] dark:text-gray-500">
                submitted {fmtDateTime(selected.submittedAt)}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4 p-4 rounded-2xl bg-[#FBFCFE] dark:bg-[#20242c] border border-[#EEF2F8] dark:border-gray-800">
              {[
                ['Category', selected.category],
                ['Location', selected.location || 'Not supplied'],
                ['Reporter', selected.reporter],
                ['Assigned to', selected.assignedLabel || selected.assignedTo || 'Manager review'],
                ['Attachments', attachmentsOf(selected).length ? `${attachmentsOf(selected).length} file${attachmentsOf(selected).length === 1 ? '' : 's'}` : 'None'],
                ['Closed', selected.closedAt ? fmtDateTime(selected.closedAt) : '—'],
              ].map(([label, value]) => (
                <div key={label} className="min-w-0">
                  <div className="text-[11px] font-bold tracking-wider text-[#94A3B8] dark:text-gray-500 uppercase">{label}</div>
                  <div className="text-[13.5px] font-semibold text-[#1E293B] dark:text-gray-200 mt-1 break-words">{value}</div>
                </div>
              ))}
            </div>

            <div>
              <h4 className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100 mb-2">Report details</h4>
              <p className="text-[13.5px] text-[#475569] dark:text-gray-300 leading-relaxed whitespace-pre-line">
                {selected.details}
              </p>
              {/* Every file on the report — opened in the page, not a new tab. */}
              {attachmentsOf(selected).length > 0 && (
                <div className="mt-3 flex flex-col gap-1.5">
                  {attachmentsOf(selected).map((a) => (
                    <button
                      key={a.url}
                      type="button"
                      onClick={() => setPreview({ url: fileHref(a.url), name: a.name, title: a.name, kind: String(a.mime || '').startsWith('image/') ? 'image' : undefined })}
                      className="inline-flex items-center gap-2 text-left text-[13px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      <Paperclip className="w-4 h-4 shrink-0" />
                      <span className="truncate">{a.name}</span>
                      {a.addedBy && <span className="font-normal text-[#94A3B8] shrink-0">· {a.addedBy}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Management controls */}
            <div className="p-4 rounded-2xl border border-[#E3E9F3] dark:border-gray-800">
              <h4 className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100 mb-3">Management controls</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-1.5">Status</label>
                  <Select
                    value={selected.status}
                    onChange={(v) => patchReport({ status: v })}
                    options={STATUS_OPTIONS}
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-1.5">Assigned to</label>
                  <Select
                    value={selected.assignedTo || ''}
                    onChange={(v) => patchReport({ assigned_to: v })}
                    options={ASSIGNEE_OPTIONS}
                    disabled={saving}
                  />
                </div>
              </div>
              {/* Priority and announcements are management's call — not on
                  the maintenance team's page (the API refuses them too). */}
              {!maintenanceOnly && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<AlertTriangle className="w-4 h-4" />}
                    disabled={saving}
                    onClick={() => patchReport({ priority: selected.priority === 'urgent' ? 'normal' : 'urgent' })}
                  >
                    {selected.priority === 'urgent' ? 'Set normal priority' : 'Mark urgent'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Megaphone className="w-4 h-4" />}
                    disabled={saving || !!selected.announcementId}
                    onClick={openAnnouncement}
                  >
                    {selected.announcementId ? 'Announcement created' : 'Create announcement'}
                  </Button>
                </div>
              )}
            </div>

            {/* Timeline */}
            <div>
              <h4 className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100 mb-3">Updates</h4>
              <div className="space-y-3.5">
                {(selected.updates || []).map((entry) => (
                  <TimelineEntry key={entry.id} entry={entry} />
                ))}
                {!selected.updates?.length && (
                  <p className="text-[13px] text-[#94A3B8] dark:text-gray-500">No updates yet.</p>
                )}
              </div>
            </div>

            {/* Reply */}
            <div className="p-4 rounded-2xl bg-[#FBFCFE] dark:bg-[#20242c] border border-[#EEF2F8] dark:border-gray-800">
              <h4 className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100 mb-2">Add update</h4>
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={3}
                maxLength={700}
                placeholder="Tell the staff member what is happening, or untick below to keep it as an internal note."
                className="w-full px-4 py-2.5 border border-[#CBD5E1] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[13.5px]"
              />
              <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
                <label className="flex items-center gap-2 text-[13px] text-[#475569] dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={replyVisible}
                    onChange={(e) => setReplyVisible(e.target.checked)}
                    className="w-4 h-4 accent-blue-600"
                  />
                  Visible to the reporter
                </label>
                <Button
                  size="sm"
                  leftIcon={<MessageSquare className="w-4 h-4" />}
                  loading={saving}
                  onClick={sendReply}
                >
                  Add update
                </Button>
              </div>
              {!replyVisible && (
                <p className="text-[12px] text-violet-600 dark:text-violet-400 mt-2 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" />
                  Internal notes stay in the dashboard — the reporter never sees them.
                </p>
              )}
            </div>
          </div>
        )}
      </Modal>

      <DocumentViewer file={preview} onClose={() => setPreview(null)} />

      {/* ── Announcement ──────────────────────────────────────────────── */}
      <Modal
        isOpen={announceOpen}
        onClose={() => setAnnounceOpen(false)}
        title="Create announcement"
        maxWidth="max-w-lg"
        footer={(
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAnnounceOpen(false)}>Cancel</Button>
            <Button leftIcon={<Megaphone className="w-4 h-4" />} loading={saving} onClick={publishAnnouncement}>
              Publish announcement
            </Button>
          </div>
        )}
      >
        <div className="space-y-4">
          <p className="text-[13px] text-[#64748B] dark:text-gray-400">
            Turns this report into a staff-wide update. It appears in Announcements and in the staff app.
          </p>
          <div>
            <label className="block text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-1.5">Audience</label>
            <Select value={announceAudience} onChange={setAnnounceAudience} options={AUDIENCE_OPTIONS} />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-1.5">Announcement</label>
            <textarea
              value={announceBody}
              onChange={(e) => setAnnounceBody(e.target.value)}
              rows={4}
              maxLength={320}
              className="w-full px-4 py-2.5 border border-[#CBD5E1] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[13.5px]"
            />
            <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">
              Check the wording is factual and contains no resident information.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StaffReports;
